import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Divider, Grid, IconButton, MenuItem, Paper, Typography, Button, Tooltip, Zoom, Dialog,
} from '@material-ui/core';
import type { AxiosError } from 'axios';
import axios from 'axios';
import useSWR, { mutate } from 'swr';
import { useSnackbar } from 'notistack';
import type { GridColDef } from '@material-ui/data-grid';
import { DataGrid } from '@material-ui/data-grid';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import * as XLSX from 'xlsx';

import DestinationImportModal from './DestinationImportModal';
import ActionMenu from '../../global/ActionMenu';
import globalUseStyles from '../../../globalClasses';

interface IDestinationBinInfo {
  id: string;
  dateModified: string;
  name: string;
  barcode: string;
  stationName: string;
  departmentName: string;
  sequence: number;
}

const AllDestinationBins: React.FC = () => {
  const navigate = useNavigate();
  const globalStyles = globalUseStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [binId, setBinId] = React.useState<string | null>(null);
  const [pageSize, setPageSize] = React.useState<number>(100);
  const [showImportModal, setShowImportModal] = React.useState<boolean>(false);

  const destinationBinFetcher = (url: string) => axios.get<Array<IDestinationBinInfo>>(url)
    .then((response) => response.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });

  const { data } = useSWR(
    '/DestinationBin/GetDestinationBins',
    (url) => destinationBinFetcher(url),
  );

  const handleActionMenuClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    if (!anchorEl) {
      setBinId(id);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleActionMenuDelete = () => {
    axios.post('/DestinationBin/DeleteBin', {
      BinId: binId,
    })
      .then(() => {
        enqueueSnackbar('Bin deleted', { variant: 'success' });
        setAnchorEl(null);
        setBinId(null);
        mutate('/DestinationBin/GetDestinationBins').catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
      })
      .catch((error: AxiosError) => {
        if (error?.message) {
          enqueueSnackbar(error.message, { persist: false, variant: 'error' });
        }
      });
  };

  const handleClose = () => {
    setBinId(null);
    setAnchorEl(null);
  };

  const displayVertIcon = (binIdParameter: string) => (
    <IconButton
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      id="table-kabob"
      onClick={(e) => handleActionMenuClick(e, binIdParameter)}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const manualAddColumns: GridColDef[] = [
    {
      field: 'sequence', headerName: 'Order', description: 'Sequence', flex: 0.33,
    },
    {
      field: 'dateModified',
      headerName: 'Date Modified',
      description: 'Date Modified',
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? (
            <Tooltip TransitionComponent={Zoom} title={new Date(value).toLocaleDateString() || 'No Date'}>
              <Box pr={4}>{new Date(value).toLocaleDateString() }</Box>
            </Tooltip>
          )
          : { value }
        )
      ),
      flex: 0.5,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 0.8,
      renderCell: ({ value }) => (
        <Tooltip TransitionComponent={Zoom} title={value || 'No Location Name'}>
          <span>
            {value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'barcode',
      headerName: 'Barcode',
      description: 'Barcode',
      flex: 0.5,
      renderCell: ({ value }) => (
        <Tooltip TransitionComponent={Zoom} title={value || 'No Barcode'}>
          <span>
            {value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'stationName',
      headerName: 'Station',
      description: 'Station',
      flex: 0.5,
      renderCell: ({ value }) => (
        <Tooltip TransitionComponent={Zoom} title={value || 'No Station'}>
          <span>
            {value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'departmentName',
      description: 'Department',
      headerName: 'Department',
      flex: 0.5,
      renderCell: ({ value }) => (
        <Tooltip TransitionComponent={Zoom} title={value || 'No Department'}>
          <span>
            {value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'id',
      type: 'string',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  const exportStationToCsvFile = (sheetData: IDestinationBinInfo[], sheetName: string) => {
    const cleanedData = sheetData.map(
      (row) => {
        const cleanedRow = {
          Id: row.id,
          Name: row.name,
          Barcode: row.barcode,
          Sequence: row.sequence,
          Station: row.stationName,
          // Department: row.departmentName,
        };
        return cleanedRow;
      },
    );
    const ws = XLSX.utils.json_to_sheet(cleanedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws);
    XLSX.writeFile(wb, `${sheetName}.csv`);
  };

  return (
    <Grid container spacing={2}>
      <Dialog open={showImportModal} fullWidth maxWidth="xl" onClose={() => setShowImportModal(false)}>
        <DestinationImportModal onClose={() => setShowImportModal(false)} />
      </Dialog>
      <Grid item xs={12} />
      <Grid item xs={12}>
        <Paper elevation={4}>
          <Grid container spacing={0} alignItems="center">
            <Grid item xs={10}>
              <Box pl={4} py={4}>
                <Typography color="primary" variant="h6">{'Locations (Locations and Destination Bins)'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box pr={2} py={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/create-location')}
                >
                  { 'Add New Location' }
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider />
              <DataGrid
                rows={data || []}
                loading={!data}
                columns={manualAddColumns}
                isCellEditable={() => false}
                autoHeight
                style={{ border: 'none' }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                className={globalStyles.transparentDataGrid}
              />
              {
                    binId && anchorEl && (
                      <ActionMenu
                        anchorEl={anchorEl}
                        handleClose={handleClose}
                      >
                        <MenuItem onClick={() => navigate(`/edit-location/${binId}`)}>{'Edit'}</MenuItem>
                        <MenuItem onClick={handleActionMenuDelete}>{'Delete'}</MenuItem>
                      </ActionMenu>
                    )
                  }
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12} container direction="row-reverse">
        <Grid item xs={2}>
          <Box pr={2} py={4}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => exportStationToCsvFile(data || [], 'Locations')}
            >
              { 'Export ' }
            </Button>
          </Box>
        </Grid>
        <Grid item xs={2}>
          <Box pr={2} py={4}>
            <Button
              fullWidth
              variant="contained"
              color="inherit"
              className={globalStyles.blueContainedButton}
              onClick={() => setShowImportModal(true)}
            >
              {'Import'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AllDestinationBins;
